/**
 * Consultas e gravações do calendário pelo TanStack Query.
 *
 * Papel: aqui ficam `useDiasDoPeriodo`, `useAusenciasDoPeriodo` e
 * `useVisitasDoPeriodo`, que mantêm em cache o que a grade lê, `useEquipe`,
 * `usePrestadores` e `useEscalaPadrao`, os cadastros da casa, e as mutações que
 * gravam e mandam o calendário inteiro se atualizar.
 *
 * Como usar: toda chave deste módulo começa por `CHAVE_CALENDARIO` e leva o
 * recorte dentro, o que faz cada mês ter a sua própria entrada de cache — voltar
 * ao mês anterior mostra o que já estava carregado, sem nova requisição. As
 * mutações invalidam o prefixo do módulo inteiro, e não o mês em que se estava:
 * uma ausência de duas semanas atravessa a virada do mês, e invalidar só o mês
 * de origem deixaria o mês seguinte mostrando a escala antiga.
 */
import {
  type CopiaDaSemana,
  type NovaAusencia,
  type NovaVisita,
  type NovoDiaCalendario,
  type NovoMembroDaEquipe,
  type NovoPrestador,
  type RecorteDoCalendario,
} from '@concierge/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  buscarEquipe,
  buscarEscalaPadrao,
  buscarPrestadores,
  cadastrarPrestador,
  incluirNaEquipe,
  removerPrestador,
  tirarDaEquipe,
} from '../api/cadastros';
import { buscarDias, copiarSemana, excluirDia, limparPeriodo, salvarDia } from '../api/dias';
import {
  atualizarAusencia,
  atualizarVisita,
  buscarAusencias,
  buscarVisitas,
  criarAusencia,
  criarVisita,
  excluirAusencia,
  excluirVisita,
} from '../api/periodos';

/** Prefixo de todas as chaves de cache deste módulo. */
export const CHAVE_CALENDARIO = ['calendario'] as const;

/**
 * Lê os dias com registro de um período.
 *
 * `habilitado` existe porque a tela monta antes de saber qual casa mostrar, e
 * uma requisição com `residenciaId` vazio volta 400 — o que apareceria como
 * erro na tela a cada primeira montagem.
 *
 * @param recorte - A casa e as duas pontas do período, ambas inclusivas.
 * @param habilitado - `false` enquanto a casa não estiver escolhida.
 * @returns Resultado da consulta, com as situações e a escala aninhadas.
 */
export function useDiasDoPeriodo(recorte: RecorteDoCalendario, habilitado = true) {
  return useQuery({
    queryKey: [...CHAVE_CALENDARIO, 'dias', recorte],
    queryFn: ({ signal }) => buscarDias(recorte, signal),
    enabled: habilitado,
  });
}

/**
 * Lê as ausências que encostam num período.
 *
 * @param recorte - A casa e as duas pontas do período, ambas inclusivas.
 * @param habilitado - `false` enquanto a casa não estiver escolhida.
 * @returns Resultado da consulta, com o colaborador aninhado em cada uma.
 */
export function useAusenciasDoPeriodo(recorte: RecorteDoCalendario, habilitado = true) {
  return useQuery({
    queryKey: [...CHAVE_CALENDARIO, 'ausencias', recorte],
    queryFn: ({ signal }) => buscarAusencias(recorte, signal),
    enabled: habilitado,
  });
}

/**
 * Lê as visitas que encostam num período.
 *
 * @param recorte - A casa e as duas pontas do período, ambas inclusivas.
 * @param habilitado - `false` enquanto a casa não estiver escolhida.
 * @returns Resultado da consulta, da visita mais antiga para a mais recente.
 */
export function useVisitasDoPeriodo(recorte: RecorteDoCalendario, habilitado = true) {
  return useQuery({
    queryKey: [...CHAVE_CALENDARIO, 'visitas', recorte],
    queryFn: ({ signal }) => buscarVisitas(recorte, signal),
    enabled: habilitado,
  });
}

/**
 * Invalida o módulo inteiro depois de uma gravação.
 *
 * É o prefixo mais raso de propósito. Um período atravessa a virada do mês, e
 * invalidar só o recorte em que se estava deixaria o mês vizinho mostrando a
 * escala de antes até alguém recarregar a página.
 *
 * @returns A função que dispara a invalidação.
 */
function useInvalidarCalendario() {
  const clienteDeConsultas = useQueryClient();
  return () => clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_CALENDARIO });
}

/**
 * Grava um dia, criando-o ou substituindo o que havia.
 *
 * @returns A mutação, que recebe o dia inteiro.
 */
export function useSalvarDia() {
  const invalidar = useInvalidarCalendario();
  return useMutation({
    mutationFn: (dados: NovoDiaCalendario) => salvarDia(dados),
    onSuccess: invalidar,
  });
}

/**
 * Apaga um dia inteiro, com as situações e a escala dele.
 *
 * @returns A mutação, que recebe o identificador do dia.
 */
export function useExcluirDia() {
  const invalidar = useInvalidarCalendario();
  return useMutation({
    mutationFn: (id: string) => excluirDia(id),
    onSuccess: invalidar,
  });
}

/** Variáveis das mutações que criam ou alteram conforme venha identificador. */
interface SalvarVariaveis<T> {
  /** Identificador do registro a alterar, ou `null` para criar um novo. */
  id: string | null;
  dados: T;
}

/**
 * Cria ou altera uma ausência, conforme venha identificador.
 *
 * @returns A mutação, que recebe `id` e os dados.
 */
export function useSalvarAusencia() {
  const invalidar = useInvalidarCalendario();
  return useMutation({
    mutationFn: ({ id, dados }: SalvarVariaveis<NovaAusencia>) =>
      id === null ? criarAusencia(dados) : atualizarAusencia(id, dados),
    onSuccess: invalidar,
  });
}

/**
 * Apaga uma ausência.
 *
 * @returns A mutação, que recebe o identificador.
 */
export function useExcluirAusencia() {
  const invalidar = useInvalidarCalendario();
  return useMutation({
    mutationFn: (id: string) => excluirAusencia(id),
    onSuccess: invalidar,
  });
}

/**
 * Cria ou altera uma visita, conforme venha identificador.
 *
 * @returns A mutação, que recebe `id` e os dados.
 */
export function useSalvarVisita() {
  const invalidar = useInvalidarCalendario();
  return useMutation({
    mutationFn: ({ id, dados }: SalvarVariaveis<NovaVisita>) =>
      id === null ? criarVisita(dados) : atualizarVisita(id, dados),
    onSuccess: invalidar,
  });
}

/**
 * Apaga uma visita.
 *
 * @returns A mutação, que recebe o identificador.
 */
export function useExcluirVisita() {
  const invalidar = useInvalidarCalendario();
  return useMutation({
    mutationFn: (id: string) => excluirVisita(id),
    onSuccess: invalidar,
  });
}

/**
 * Apaga os dias de um período — o "Limpar mês inteiro".
 *
 * @returns A mutação, que recebe a casa e as duas pontas do período.
 */
export function useLimparPeriodo() {
  const invalidar = useInvalidarCalendario();
  return useMutation({
    mutationFn: (recorte: RecorteDoCalendario) => limparPeriodo(recorte),
    onSuccess: invalidar,
  });
}

/**
 * Copia uma semana sobre a seguinte, com os períodos marcados.
 *
 * @returns A mutação, que recebe o pedido da caixa "Copiar semana anterior".
 */
export function useCopiarSemana() {
  const invalidar = useInvalidarCalendario();
  return useMutation({
    mutationFn: (dados: CopiaDaSemana) => copiarSemana(dados),
    onSuccess: invalidar,
  });
}

/**
 * Lê quem está na escala da casa.
 *
 * @param residenciaId - A casa do calendário aberto.
 * @param habilitado - `false` enquanto a casa não estiver escolhida.
 * @returns Resultado da consulta, na ordem em que cada pessoa entrou na escala.
 */
export function useEquipe(residenciaId: string, habilitado = true) {
  return useQuery({
    queryKey: [...CHAVE_CALENDARIO, 'equipe', residenciaId],
    queryFn: ({ signal }) => buscarEquipe(residenciaId, signal),
    enabled: habilitado,
  });
}

/**
 * Põe uma pessoa na escala da casa.
 *
 * @returns A mutação, que recebe a casa e o colaborador.
 */
export function useIncluirNaEquipe() {
  const invalidar = useInvalidarCalendario();
  return useMutation({
    mutationFn: (dados: NovoMembroDaEquipe) => incluirNaEquipe(dados),
    onSuccess: invalidar,
  });
}

/**
 * Tira uma pessoa da escala da casa.
 *
 * @returns A mutação, que recebe o identificador da linha da escala.
 */
export function useTirarDaEquipe() {
  const invalidar = useInvalidarCalendario();
  return useMutation({
    mutationFn: (id: string) => tirarDaEquipe(id),
    onSuccess: invalidar,
  });
}

/**
 * Lê os prestadores esporádicos da casa.
 *
 * @param residenciaId - A casa do calendário aberto.
 * @param habilitado - `false` enquanto a casa não estiver escolhida.
 * @returns Resultado da consulta, na ordem em que cada um entrou na lista.
 */
export function usePrestadores(residenciaId: string, habilitado = true) {
  return useQuery({
    queryKey: [...CHAVE_CALENDARIO, 'prestadores', residenciaId],
    queryFn: ({ signal }) => buscarPrestadores(residenciaId, signal),
    enabled: habilitado,
  });
}

/**
 * Cadastra um prestador esporádico.
 *
 * @returns A mutação, que recebe a casa e o nome.
 */
export function useCadastrarPrestador() {
  const invalidar = useInvalidarCalendario();
  return useMutation({
    mutationFn: (dados: NovoPrestador) => cadastrarPrestador(dados),
    onSuccess: invalidar,
  });
}

/**
 * Tira um prestador da lista.
 *
 * @returns A mutação, que recebe o identificador do prestador.
 */
export function useRemoverPrestador() {
  const invalidar = useInvalidarCalendario();
  return useMutation({
    mutationFn: (id: string) => removerPrestador(id),
    onSuccess: invalidar,
  });
}

/**
 * Lê a escala padrão de cada dia da semana da casa.
 *
 * @param residenciaId - A casa do calendário aberto.
 * @param habilitado - `false` enquanto a casa não estiver escolhida.
 * @returns Resultado da consulta, um item por dia da semana que tem padrão.
 */
export function useEscalaPadrao(residenciaId: string, habilitado = true) {
  return useQuery({
    queryKey: [...CHAVE_CALENDARIO, 'escala-padrao', residenciaId],
    queryFn: ({ signal }) => buscarEscalaPadrao(residenciaId, signal),
    enabled: habilitado,
  });
}
